import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';

export default function CustomDevelopmentPage() {
  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-blue-50 via-purple-50 to-white pt-32 pb-20">
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-full blur-3xl"></div>
            <div className="absolute bottom-20 right-10 w-96 h-96 bg-gradient-to-br from-purple-400/20 to-blue-400/20 rounded-full blur-3xl"></div>
          </div>
          
          <div className="max-w-7xl mx-auto px-6 relative z-10">
            <div className="max-w-3xl">
              <div className="inline-block px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white text-xs font-semibold rounded-full mb-6">
                AI SOLUTIONS
              </div>
              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                Custom Development
                <span className="block bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Solutions
                </span>
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed mb-8">
                Tailored AI solutions built specifically for your unique business needs. From concept to deployment, we create custom applications that drive results.
              </p>
              <div className="flex flex-wrap gap-4">
                <a href="/contact" className="px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold hover:shadow-xl transition-all duration-300 whitespace-nowrap cursor-pointer">
                  Start Your Project
                </a>
                <a href="/case-studies" className="px-8 py-4 bg-white border-2 border-gray-200 text-gray-700 rounded-lg font-semibold hover:border-purple-500 transition-all duration-300 whitespace-nowrap cursor-pointer">
                  View Portfolio
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Services */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <div className="inline-block px-4 py-2 bg-gradient-to-r from-blue-100 to-purple-100 text-transparent bg-clip-text text-sm font-semibold rounded-full mb-4">
                DEVELOPMENT SERVICES
              </div>
              <h2 className="text-4xl font-bold mb-4">What We Build</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                End-to-end custom development services powered by AI
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  icon: 'ri-smartphone-line',
                  title: 'AI-Powered Applications',
                  description: 'Custom web and mobile applications with integrated AI capabilities for intelligent user experiences.',
                  gradient: 'from-blue-500 to-cyan-500'
                },
                {
                  icon: 'ri-code-box-line',
                  title: 'API Development',
                  description: 'Robust APIs and microservices that enable seamless integration with your existing systems.',
                  gradient: 'from-purple-500 to-pink-500'
                },
                {
                  icon: 'ri-chat-4-line',
                  title: 'Chatbot Solutions',
                  description: 'Intelligent conversational interfaces for customer service, sales, and internal operations.',
                  gradient: 'from-indigo-500 to-blue-500'
                },
                {
                  icon: 'ri-cloud-line',
                  title: 'Cloud Solutions',
                  description: 'Scalable cloud-native applications with AI/ML capabilities on AWS, Azure, or GCP.',
                  gradient: 'from-violet-500 to-purple-500'
                },
                {
                  icon: 'ri-links-line',
                  title: 'System Integration',
                  description: 'Connect disparate systems and data sources into unified, intelligent workflows.',
                  gradient: 'from-blue-500 to-indigo-500'
                },
                {
                  icon: 'ri-tools-line',
                  title: 'Legacy Modernization',
                  description: 'Transform outdated systems with modern AI-enhanced architectures and interfaces.',
                  gradient: 'from-purple-500 to-violet-500'
                }
              ].map((service, index) => (
                <div 
                  key={index}
                  className="group bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-2xl hover:-translate-y-2 transition-all duration-300"
                >
                  <div className={`w-14 h-14 flex items-center justify-center bg-gradient-to-br ${service.gradient} rounded-xl mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <i className={`${service.icon} text-2xl text-white`}></i>
                  </div>
                  <h3 className="text-xl font-bold mb-3">{service.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{service.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Development Process */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <div className="inline-block px-4 py-2 bg-gradient-to-r from-blue-100 to-purple-100 text-transparent bg-clip-text text-sm font-semibold rounded-full mb-4">
                OUR PROCESS
              </div>
              <h2 className="text-4xl font-bold mb-4">How We Work</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                A proven agile methodology for delivering high-quality custom solutions
              </p>
            </div>

            <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-6">
              {[
                {
                  phase: 'Discovery',
                  icon: 'ri-compass-3-line',
                  description: 'Understand requirements and define scope'
                },
                {
                  phase: 'Design',
                  icon: 'ri-pencil-ruler-2-line',
                  description: 'Create architecture and user experience'
                },
                {
                  phase: 'Development',
                  icon: 'ri-code-s-slash-line',
                  description: 'Build with agile sprints and iterations'
                },
                {
                  phase: 'Testing',
                  icon: 'ri-bug-line',
                  description: 'Rigorous QA and performance testing'
                },
                {
                  phase: 'Deployment',
                  icon: 'ri-rocket-line',
                  description: 'Launch to production environment'
                },
                {
                  phase: 'Support',
                  icon: 'ri-customer-service-line',
                  description: 'Ongoing maintenance and optimization'
                }
              ].map((phase, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-2xl p-6 border border-gray-200 hover:shadow-xl transition-all duration-300 text-center"
                >
                  <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-blue-500 to-purple-500 rounded-full mx-auto mb-4">
                    <i className={`${phase.icon} text-xl text-white`}></i>
                  </div>
                  <h3 className="text-lg font-bold mb-2">{phase.phase}</h3>
                  <p className="text-gray-600 text-sm">{phase.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Technology Stack */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <div className="inline-block px-4 py-2 bg-gradient-to-r from-blue-100 to-purple-100 text-transparent bg-clip-text text-sm font-semibold rounded-full mb-4">
                TECHNOLOGY STACK
              </div>
              <h2 className="text-4xl font-bold mb-4">Technologies We Master</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Cutting-edge tools and frameworks for building robust solutions
              </p>
            </div>

            <div className="grid md:grid-cols-4 gap-8">
              {[
                {
                  category: 'Frontend',
                  icon: 'ri-layout-line',
                  techs: ['React', 'Vue.js', 'Angular', 'Next.js']
                },
                {
                  category: 'Backend',
                  icon: 'ri-server-line',
                  techs: ['Node.js', 'Python', 'Java', '.NET']
                },
                {
                  category: 'AI/ML',
                  icon: 'ri-brain-line',
                  techs: ['TensorFlow', 'PyTorch', 'OpenAI', 'Hugging Face']
                },
                {
                  category: 'Cloud',
                  icon: 'ri-cloud-line',
                  techs: ['AWS', 'Azure', 'GCP', 'Docker']
                }
              ].map((stack, index) => (
                <div 
                  key={index}
                  className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-8 hover:shadow-xl transition-all duration-300"
                >
                  <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg mb-4">
                    <i className={`${stack.icon} text-xl text-white`}></i>
                  </div>
                  <h3 className="text-xl font-bold mb-4">{stack.category}</h3>
                  <ul className="space-y-2">
                    {stack.techs.map((tech, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-gray-700">
                        <div className="w-1.5 h-1.5 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"></div>
                        {tech}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <div className="inline-block px-4 py-2 bg-gradient-to-r from-blue-100 to-purple-100 text-transparent bg-clip-text text-sm font-semibold rounded-full mb-4">
                WHY FLUXBYTE
              </div>
              <h2 className="text-4xl font-bold mb-4">Why Choose Our Development Team</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                What sets us apart in custom AI development
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                {
                  icon: 'ri-user-star-line',
                  title: 'Expert Team',
                  description: 'Senior developers with deep AI/ML expertise'
                },
                {
                  icon: 'ri-speed-line',
                  title: 'Agile Delivery',
                  description: 'Fast iterations with continuous feedback'
                },
                {
                  icon: 'ri-shield-check-line',
                  title: 'Quality Assured',
                  description: 'Rigorous testing and code review processes'
                },
                {
                  icon: 'ri-customer-service-2-line',
                  title: 'Full Support',
                  description: 'Ongoing maintenance and enhancement'
                }
              ].map((benefit, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-2xl p-8 border border-gray-200 text-center hover:shadow-xl transition-all duration-300"
                >
                  <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-blue-500 to-purple-500 rounded-full mx-auto mb-6">
                    <i className={`${benefit.icon} text-2xl text-white`}></i>
                  </div>
                  <h3 className="text-lg font-bold mb-2">{benefit.title}</h3>
                  <p className="text-gray-600 text-sm">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-4xl font-bold mb-6">
              Ready to Build Your
              <span className="block bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Custom AI Solution?
              </span>
            </h2>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Let's discuss your project requirements and create something extraordinary together.
            </p>
            <a 
              href="/contact"
              className="inline-block px-10 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold hover:shadow-xl transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Schedule a Consultation
            </a>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
}